import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { OutlinedButton } from 'components/Button';
import GridLayoutEditor from 'app/components/GridLayoutEditor';
import ActionDropdownMenu from 'components/base/ActionDropdownMenu';
import SVG from 'components/base/SVG';
import { isPropStream } from 'utils/brand';
import { PropertyGroupTypes } from 'data/property';
import Options from '../Group/Options';
import useViewportSize from 'utils/useViewportSize';
import Tooltip from 'app/components/Tooltip';
import dropdownCss from 'components/base/ActionDropdownMenu/style.scss';

import css from './style.scss';

/**
 * Controls / header block for the My Properties (Property Group) dashboard.
 *
 * Extracted verbatim from `app/Property/Group/index.js` render(). All behavior
 * is preserved; every value and handler it needs is passed in as a prop so the
 * parent class component remains the single source of truth for state/logic.
 */
const MyPropertiesControls = ({
  // flags / values
  add,
  mobile,
  all,
  loading,
  // A background export is queued, polled or being confirmed: shown on the Export button only.
  exportInProgress,
  selected,
  folderEnabled,
  folderKey,
  customGroup,
  context,
  topRecords,
  lmEnabled,
  monitored,
  fullAccess,
  name,
  size,
  id,
  monitorTransferLists,
  columnApi,
  layoutId,
  layouts,
  // grid instance helpers
  getFixedColumns,
  columns,
  gridApi,
  // handlers
  handleAddToGroup,
  handleRemove,
  handleImport,
  handleExport,
  openBatchDialerModal,
  handleOpenContractManager,
  handleOpenMap,
  handleGenerateMailingLabels,
  handleRename,
  handleDeleteGroup,
  handleEditFilter,
  handleAddToFolder,
  handleDeleteFolder,
  handleEditFolder,
  handleNewCampaign,
  handleSkipClick,
  handleViewFilter,
  handleGridLayoutApply,
}) => {
  const { isMedium, isSmall, width } = useViewportSize();
  const titleRef = useRef(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    setIsTitleTruncated(!!el && el.scrollWidth > el.clientWidth);
  }, [name, selected, width]);
  // Items for the Actions dropdown
  const actionItems = [
    // Push to BatchDialer - with custom SVG icon
    isPropStream()
      ? {
          label: 'Push to BatchDialer',
          icon: <SVG icon="push-to-batch" />,
          onSelect: openBatchDialerModal,
          disabled: !selected,
        }
      : null,

    { divider: true },

    // Manage Contracts - with SVG icon
    mobile
      ? null
      : {
          label: 'Manage Contracts',
          icon: <SVG icon="contract" />,
          onSelect: handleOpenContractManager,
          disabled: !selected,
        },

    // View Map - with SVG icon
    mobile || !topRecords.length
      ? null
      : {
          label: 'View Map',
          icon: <SVG icon="map" />,
          onSelect: handleOpenMap,
          disabled: !selected,
        },

    // Export CSV - with SVG icon
    !mobile && !add
      ? {
          label: 'Export CSV',
          icon: <SVG icon="export-csv" />,
          onSelect: () => handleExport(true),
          disabled: !selected,
        }
      : null,

    // Generate Mailing Labels - with SVG icon
    {
      label: 'Generate Mailing Labels',
      icon: <SVG icon="generate-mail" />,
      onSelect: handleGenerateMailingLabels,
      disabled: !selected,
    },

    // Divider
    { divider: true },

    // Add to List - with SVG icon and sub-items (expands in-place)
    {
      label: 'Add to List',
      icon: <SVG icon="add-to-list" />,
      disabled: !selected,
      subItems: [
        {
          label: 'Add to Favorites List',
          icon: <SVG icon="add" />,
          onSelect: () => handleAddToGroup(PropertyGroupTypes.FAVORITE),
          disabled: !selected,
        },
        {
          label: 'Add to Marketing List',
          icon: <SVG icon="add" />,
          onSelect: () => handleAddToGroup(PropertyGroupTypes.MARKETING),
          disabled: !selected,
        },
      ].filter(Boolean),
    },

    customGroup && !monitored
      ? {
          label: `View List Filters`,
          icon: <SVG icon="edit-filter" />,
          onSelect: () => handleViewFilter(),
        }
      : null,

    customGroup && lmEnabled && monitored
      ? { label: 'Edit List Filters', icon: <SVG icon="edit-filter" />, onSelect: handleEditFilter }
      : null,

    // Rename Group - conditional
    !mobile && customGroup && !context.getIn(['info', 'defaultGroup'])
      ? {
          label: `Rename This List`,
          icon: <SVG icon="icon-edit" />,
          onSelect: handleRename,
        }
      : null,

    // Delete Group - conditional
    !mobile && customGroup
      ? {
          label: `Delete This List`,
          icon: <SVG icon="delete-green" />,
          onSelect: () => handleDeleteGroup(),
        }
      : null,

    // Divider
    !folderEnabled ? null : { divider: true },

    // Add/Move to Folder - conditional
    !folderEnabled
      ? null
      : {
          label: `${folderKey ? 'Move' : 'Add'} List to Folder`,
          icon: <SVG icon={folderKey ? 'move-list-folder' : 'icon-add-folder'} />,
          onSelect: () => handleAddToFolder(folderKey),
        },

    // Remove/delete Folder - conditional
    !folderEnabled || (folderKey || '') === ''
      ? null
      : {
          label: 'Remove Folder',
          icon: <SVG icon="remove-folder" />,
          onSelect: () => handleDeleteFolder(folderKey),
        },

    // Edit Folder - conditional
    !folderEnabled || (folderKey || '') === ''
      ? null
      : {
          label: 'Edit Folder',
          icon: <SVG icon="edit-folder" />,
          onSelect: () => handleEditFolder(folderKey),
        },

    // Divider
    selected ? { divider: true } : null,

    // Delete Selection - with SVG icon
    selected
      ? {
          label: 'Delete Selection',
          icon: <SVG icon="delete-red" />,
          onSelect: handleRemove,
          disabled: !selected,
        }
      : null,
  ];

  // Items for the New Campaign dropdown
  const campaignItems = [
    {
      label: 'Dialer',
      subtitle: 'Start a New Dialer Campaign Activity',
      icon: <SVG icon="dialer-icon" />,
      onSelect: () => handleNewCampaign('DIALER'),
    },
    {
      label: 'Direct Mail',
      subtitle: 'Start a New Direct Mail Campaign Activity',
      icon: <SVG icon="direct-mail-icon" />,
      iconStyle: 'iconSizeLargeSecondary',
      onSelect: () => handleNewCampaign('DIRECT MAIL'),
    },
    {
      label: 'Email',
      subtitle: 'Start a New Email Campaign Activity',
      icon: <SVG icon="email-icon" />,
      iconStyle: 'iconSizeLargeSecondary',
      onSelect: () => handleNewCampaign('EMAIL'),
    },
  ];

  return (
    <div className={css.controlsContainer}>
      <div className={css.infoGroup}>
        <div>{!selected ? <div /> : <div className={css.selected}>{selected} Selected</div>}</div>
        <div className={css.countContainer} style={{ flexDirection: selected ? 'row' : 'column' }}>
          {isTitleTruncated ? (
            <Tooltip content={name} position="bottom" arrowOffset="50%" yOffset={-10}>
              <div ref={titleRef} className={classNames({ [css.title]: selected, [css.titleSelected]: !selected })}>
                {name}
              </div>
            </Tooltip>
          ) : (
            <div ref={titleRef} className={classNames({ [css.title]: selected, [css.titleSelected]: !selected })}>
              {name}
            </div>
          )}
          <div
            className={classNames({
              [css.titleQuantity]: selected,
              [css.titleQuantitySelected]: !selected,
            })}
          >
            ({size})
          </div>
        </div>
      </div>
      <div className={css.controlWrapper}>
        <div className={css.controlGroup}>
          {
            <OutlinedButton
              loading={loading}
              icon="myproperty-import"
              onlyIcon={isMedium || isSmall}
              text="Import"
              onClick={handleImport}
            />
          }
          {add || mobile ? null : (
            <OutlinedButton
              icon="myproperty-export"
              onlyIcon={isMedium || isSmall}
              loading={loading || exportInProgress}
              disabled={!selected}
              text="Export"
              onClick={() => handleExport(false)}
            />
          )}
        </div>
        <div className={css.controlGroup}>
          {!all || !fullAccess || mobile ? null : (
            // For the campaign dropdown
            <ActionDropdownMenu
              text="New Campaign"
              icon="iconAdd"
              dropdownClassName={dropdownCss.campaignDropdown}
              leftAnchored={false}
              minWidth={386}
              iconSize="large"
              items={campaignItems}
              trigger={
                <OutlinedButton
                  icon="myproperty-campaign-activity"
                  loading={loading}
                  onlyIcon={isSmall}
                  text="New Campaign Activity"
                />
              }
            />
          )}
          {lmEnabled && (
            // Accounts without PropStream Connect (lmEnabled false) don't get
            // Automate List — hide it entirely rather than showing a disabled button.
            <Options
              loading={loading}
              lists={monitorTransferLists}
              context={context}
              trigger={
                <OutlinedButton
                  icon="myproperty-automate-list"
                  onlyIcon={isSmall}
                  loading={loading}
                  text="Automate List"
                />
              }
            />
          )}
          <OutlinedButton
            icon="myproperty-skip-trace"
            loading={loading}
            orangeTheme
            disabled={!selected}
            onlyIcon={isSmall}
            text="Skip Trace"
            onClick={() => handleSkipClick()}
          />
          <ActionDropdownMenu
            text="Actions"
            icon="iconCheckSquare"
            loading={loading}
            showHoverState={!selected}
            hoverMessage="Select Item(s) First"
            items={actionItems}
            trigger={
              <OutlinedButton
                rightIcon="myproperty-arrow-down"
                solidPrimary
                loading={loading}
                text="Actions"
              />
            }
          />
        </div>
        <div>
          <GridLayoutEditor
            fixedColumns={getFixedColumns()}
            columns={columns.map((c) => ({
              ...c,
              field: c.path,
              unavailable: c.lmRequired && !lmEnabled,
            }))}
            onApply={handleGridLayoutApply}
            skipCatcherCheck
            type="PROPERTY_GRID"
            gridApi={gridApi}
            columnApi={columnApi}
            layoutId={layoutId}
            defaultName="Property Layout"
            savedPropertyGroupId={id}
            layouts={layouts}
          />
        </div>
      </div>
    </div>
  );
};

export default MyPropertiesControls;
